import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import IdleTimeout from "@/components/IdleTimeout";
import { SupportWidget } from "@/components/SupportWidget";
import { Layout } from "@/components/Layout";
import Home from "@/pages/marketing/Home";
import ProcessServing from "@/pages/marketing/ProcessServing";
import NotaryServices from "@/pages/marketing/NotaryServices";
import ServiceAreas from "@/pages/marketing/ServiceAreas";
import About from "@/pages/marketing/About";
import AgencyAccess from "@/pages/marketing/AgencyAccess";
import Contact from "@/pages/marketing/Contact";
import Faq from "@/pages/marketing/Faq";
import AgencySite from "@/pages/agency/AgencySite";
import { api as _rootApi } from "@/lib/api";

function RootLanding() {
  const [mode, setMode] = React.useState(() => {
    const h = window.location.hostname;
    return /emergentagent|localhost|127\.0\.0\.1/.test(h) ? "home" : "resolving";
  });
  React.useEffect(() => {
    if (mode !== "resolving") return;
    _rootApi.get(`/agencies/resolve?host=${window.location.hostname}`)
      .then(() => setMode("agency")).catch(() => setMode("home"));
  }, [mode]);
  if (mode === "resolving") return null;
  return mode === "agency" ? <AgencySite /> : <Home />;
}
import AdminAgencies from "@/pages/admin/AdminAgencies";
import AdminIntake from "@/pages/admin/AdminIntake";
import Login from "@/pages/Login";
import Profile from "@/pages/Profile";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Unsubscribe from "@/pages/Unsubscribe";
import ClientDashboard from "@/pages/client/ClientDashboard";
import CreateOrder from "@/pages/client/CreateOrder";
import ServiceSelect from "@/pages/client/ServiceSelect";
import NotaryRequest from "@/pages/client/NotaryRequest";
import NotaryPay from "@/pages/NotaryPay";
import NotaryConfirmation from "@/pages/NotaryConfirmation";
import AdminNotary from "@/pages/admin/AdminNotary";
import ClientOrderConfirmation from "@/pages/client/ClientOrderConfirmation";
import ClientHistory from "@/pages/client/ClientHistory";
import ClientInvoices from "@/pages/client/ClientInvoices";
import StripeReturn from "@/pages/client/StripeReturn";
import ClientOnboarding from "@/pages/client/ClientOnboarding";
import OrderDetail from "@/pages/OrderDetail";
import ServerDashboard from "@/pages/server/ServerDashboard";
import NotaryAssignments from "@/pages/server/NotaryAssignments";
import NotaryJobDetail from "@/pages/server/NotaryJobDetail";
import ServerJobDetail from "@/pages/server/ServerJobDetail";
import ServerPayouts from "@/pages/server/ServerPayouts";
import ServerOnboarding from "@/pages/server/ServerOnboarding";
import ConnectReturn from "@/pages/server/ConnectReturn";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminClients from "@/pages/admin/AdminClients";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminServers from "@/pages/admin/AdminServers";
import AdminDemoEnvironments from "@/pages/admin/AdminDemoEnvironments";
import AdminServerDetail from "@/pages/admin/AdminServerDetail";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import AdminVerifyReport from "@/pages/admin/AdminVerifyReport";import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminEvidenceIssues from "@/pages/admin/AdminEvidenceIssues";
import AdminDocumentRequests from "@/pages/admin/AdminDocumentRequests";
import AdminBranding from "@/pages/admin/AdminBranding";
import AdminAgencyOnboarding from "@/pages/admin/AdminAgencyOnboarding";
import AgencyPay from "@/pages/AgencyPay";
import AgencyPayResult from "@/pages/AgencyPayResult";
import AgencyActivate from "@/pages/AgencyActivate";
import AgencyDashboard from "@/pages/agency/AgencyDashboard";
import AgencyConnectReturn from "@/pages/agency/AgencyConnectReturn";

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-500" />
    </div>
  );
}

function homeFor(role) {
  return role === "admin" ? "/admin" : role === "server" ? "/server" : role === "agency" ? "/agency" : "/client";
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Loader />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootLanding />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/process-serving" element={<ProcessServing />} />
      <Route path="/notary-services" element={<NotaryServices />} />
      <Route path="/service-areas" element={<ServiceAreas />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/about" element={<About />} />
      <Route path="/agency-access" element={<AgencyAccess />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/a/:slug" element={<AgencySite />} />
      <Route path="/agency/pay/success" element={<AgencyPayResult mode="success" />} />
      <Route path="/agency/pay/cancel" element={<AgencyPayResult mode="cancel" />} />
      <Route path="/agency/pay/:token" element={<AgencyPay />} />
      <Route path="/agency/activate/:token" element={<AgencyActivate />} />
      <Route path="/agency" element={<Protected roles={["agency"]}><AgencyDashboard /></Protected>} />
      <Route path="/agency/connect/return" element={<Protected roles={["agency"]}><AgencyConnectReturn /></Protected>} />
      <Route path="/agency/connect/refresh" element={<Protected roles={["agency"]}><AgencyConnectReturn /></Protected>} />
      <Route path="/agency/billing/return" element={<Protected roles={["agency"]}><AgencyConnectReturn /></Protected>} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/profile" element={<Protected roles={["client", "server", "admin"]}><Profile /></Protected>} />

      <Route path="/client" element={<Protected roles={["client"]}><ClientDashboard /></Protected>} />
      <Route path="/client/onboarding" element={<Protected roles={["client"]}><ClientOnboarding /></Protected>} />
      <Route path="/client/new" element={<Protected roles={["client"]}><CreateOrder /></Protected>} />
      <Route path="/client/start" element={<Protected roles={["client"]}><ServiceSelect /></Protected>} />
      <Route path="/client/notary" element={<Protected roles={["client"]}><NotaryRequest /></Protected>} />
      <Route path="/notary" element={<NotaryRequest />} />
      <Route path="/notary/confirmation" element={<NotaryConfirmation />} />
      <Route path="/notary/pay/:token" element={<NotaryPay />} />
      <Route path="/client/history" element={<Protected roles={["client"]}><ClientHistory /></Protected>} />
      <Route path="/client/invoices" element={<Protected roles={["client"]}><ClientInvoices /></Protected>} />
      <Route path="/payment/success" element={<Protected roles={["client"]}><StripeReturn mode="success" /></Protected>} />
      <Route path="/payment/cancel" element={<Protected roles={["client"]}><StripeReturn mode="cancel" /></Protected>} />
      <Route path="/client/orders/:id/confirmation" element={<Protected roles={["client"]}><ClientOrderConfirmation /></Protected>} />
      <Route path="/client/orders/:id" element={<Protected roles={["client"]}><OrderDetail /></Protected>} />

      <Route path="/server" element={<Protected roles={["server"]}><ServerDashboard /></Protected>} />
      <Route path="/server/history" element={<Protected roles={["server"]}><ServerDashboard historyMode /></Protected>} />
      <Route path="/server/onboarding" element={<Protected roles={["server"]}><ServerOnboarding /></Protected>} />
      <Route path="/server/payouts" element={<Protected roles={["server"]}><ServerPayouts /></Protected>} />
      <Route path="/server/connect/return" element={<Protected roles={["server"]}><ConnectReturn mode="return" /></Protected>} />
      <Route path="/server/connect/refresh" element={<Protected roles={["server"]}><ConnectReturn mode="refresh" /></Protected>} />
      <Route path="/server/jobs/:id" element={<Protected roles={["server"]}><ServerJobDetail /></Protected>} />
      <Route path="/server/notary" element={<Protected roles={["server"]}><NotaryAssignments /></Protected>} />
      <Route path="/server/notary/:id" element={<Protected roles={["server"]}><NotaryJobDetail /></Protected>} />

      <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
      <Route path="/admin/jobs" element={<Protected roles={["admin"]}><AdminJobs /></Protected>} />
      <Route path="/admin/jobs/:id" element={<Protected roles={["admin"]}><OrderDetail /></Protected>} />
      <Route path="/admin/clients" element={<Protected roles={["admin"]}><AdminClients /></Protected>} />
      <Route path="/admin/pricing" element={<Protected roles={["admin"]}><AdminPricing /></Protected>} />
      <Route path="/admin/clients/:id" element={<Protected roles={["admin"]}><AdminClientDetail /></Protected>} />
      <Route path="/admin/servers" element={<Protected roles={["admin"]}><AdminServers /></Protected>} />
      <Route path="/admin/demo" element={<Protected roles={["admin"]}><AdminDemoEnvironments /></Protected>} />
      <Route path="/admin/servers/:id" element={<Protected roles={["admin"]}><AdminServerDetail /></Protected>} />
      <Route path="/admin/billing" element={<Protected roles={["admin"]}><AdminBilling /></Protected>} />
      <Route path="/admin/payouts" element={<Protected roles={["admin"]}><AdminPayouts /></Protected>} />
      <Route path="/admin/verify-report" element={<Protected roles={["admin"]}><AdminVerifyReport /></Protected>} />
      <Route path="/admin/documents" element={<Protected roles={["admin"]}><AdminDocuments /></Protected>} />
      <Route path="/admin/evidence-issues" element={<Protected roles={["admin"]}><AdminEvidenceIssues /></Protected>} />
      <Route path="/admin/requests" element={<Protected roles={["admin"]}><AdminDocumentRequests /></Protected>} />
      <Route path="/admin/branding" element={<Protected roles={["admin"]}><AdminBranding /></Protected>} />
      <Route path="/admin/agencies" element={<Protected roles={["admin"]}><AdminAgencies /></Protected>} />
      <Route path="/admin/agency-onboarding" element={<Protected roles={["admin"]}><AdminAgencyOnboarding /></Protected>} />
      <Route path="/admin/intake" element={<Protected roles={["admin"]}><AdminIntake /></Protected>} />
      <Route path="/admin/notary" element={<Protected roles={["admin"]}><AdminNotary /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <IdleTimeout />
        <AppRoutes />
        <SupportWidget />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
